import { useMemo } from 'react'

import { ObservableQuery } from '@apollo/client'
import { useSelector } from 'react-redux'
import styled from 'styled-components'

import { OrderReviewGigaQueryQuery } from '__generated__/atlas-types'

import { useScreenWidth } from 'hooks/screenHooks'
import { isAccountTypeBrand as isAccountTypeBrandSelector } from 'store/currentUser/selectors'

import {
  AddressesWidget,
  LegalWidget,
  NotesWidget,
  OrderDetailsWidget,
  OrderMinimumsWidget,
  ProductsWidget,
} from './components'
import { FinancialSummaryContainer } from './components/FinancialSummaryWidget/FinancialSummaryWidget'
import {
  FetchMoreFn,
  Product,
  ProductAmounts,
} from './components/ProductsWidget/types'
import { ContactsWidget } from 'features/Orders/components'

export type OrderData = OrderReviewGigaQueryQuery['orders']['edges'][0]['node']

export const Overview = ({
  isJoorPayEnabled,
  setIsProductsModalOpen,
  loading,
  refetch,
  orderData,
  products,
  productAmounts,
  fetchAllProducts,
}: {
  isJoorPayEnabled: boolean
  setIsProductsModalOpen: () => void
  loading: boolean
  refetch: ObservableQuery['refetch']
  orderData: OrderData
  products: Product[]
  productAmounts: ProductAmounts
  fetchAllProducts: FetchMoreFn
}) => {
  const isAccountTypeBrand = useSelector(isAccountTypeBrandSelector)
  const screenWidth = useScreenWidth()

  const { termsAndConditions } = orderData

  const hasTerms = !!termsAndConditions?.terms

  const minAmount = orderData?.brand?.orderSettings?.minAmount || 0
  const minUnits = orderData?.brand?.orderSettings?.minAmount || 0

  const hasOrderMinimums = !!minAmount && !!minUnits

  const columns = useMemo(() => {
    // TODO: The sm layout has to be updated to match the new design
    const breakpoint =
      (screenWidth < 1024 && 'sm') || (screenWidth < 1920 && 'md') || 'lg'

    if (isAccountTypeBrand) {
      if (hasTerms) {
        return hasOrderMinimums
          ? proBrandWithOrderMinimumsColumns[breakpoint]
          : proBrandWithoutMinimumsColumns[breakpoint]
      }

      return hasOrderMinimums
        ? proBrandWithOrderMinimumsWithoutLegalColumns[breakpoint]
        : proBrandWithoutMinimumsAndLegalColumns[breakpoint]
    } else {
      if (hasTerms) {
        return hasOrderMinimums
          ? liteRetailerWithOrderMinimumsColumns[breakpoint]
          : liteRetailerWithoutMinimumsColumns[breakpoint]
      }

      return hasOrderMinimums
        ? liteRetailerWithOrderMinimumsAndWithoutLegalColumns[breakpoint]
        : liteRetailerWithoutMinimumsAndLegalColumns[breakpoint]
    }
  }, [hasOrderMinimums, isAccountTypeBrand, screenWidth, hasTerms])

  return (
    <div>
      <Container>
        {columns.map((rows, columnIndex) => (
          <Column key={`col-${columnIndex}`}>
            {rows.map((elements, rowIndex) => (
              <Row key={`col-${columnIndex}-row-${rowIndex}`}>
                {elements.map((element, index) => (
                  <Widget
                    key={`col-${columnIndex}-row-${rowIndex}-widget-${index}`}
                    type={element}
                    isJoorPayEnabled={isJoorPayEnabled}
                    setIsProductsModalOpen={setIsProductsModalOpen}
                    loading={loading}
                    refetch={refetch}
                    orderFullInfo={orderData}
                    productAmounts={productAmounts}
                    products={products}
                    fetchAllProducts={fetchAllProducts}
                  />
                ))}
              </Row>
            ))}
          </Column>
        ))}
      </Container>
    </div>
  )
}

const TEST_IDS = {
  CONTACTS: 'contacts-widget',
  PRODUCTS: 'products-list-widget',
  ORDER_MINIMUMS: 'order-minimums-widget',
  FINANCIAL_SUMMARY: 'financial-summary-widget',
  PRICE_CHANGES: 'price-changes-widget',
  ADDRESSES: 'addresses-widget',
  ORDER_DETAILS: 'order-details-widget',
  LEGAL: 'legal-widget',
  NOTES: 'notes-widget',
} as const
type WidgetType = keyof typeof TEST_IDS | '_blank'

const Widget = ({
  type,
  setIsProductsModalOpen,
  loading,
  refetch,
  orderFullInfo,
  products,
  productAmounts,
  fetchAllProducts,
}: {
  type: WidgetType
  isJoorPayEnabled: boolean
  setIsProductsModalOpen: () => void
  loading: boolean
  refetch: ObservableQuery['refetch']
  orderFullInfo?: OrderData
  products: Product[]
  productAmounts: ProductAmounts
  fetchAllProducts: FetchMoreFn
}) => {
  const testId = type !== '_blank' ? TEST_IDS[type] : ''
  switch (type) {
    case 'CONTACTS':
      return (
        <StyledContactsWidget
          brand={orderFullInfo?.brand}
          retailerProfile={orderFullInfo?.retailerProfile}
          status={orderFullInfo?.status}
          salesCommissions={orderFullInfo?.salesComissions}
          buyer={orderFullInfo?.buyer}
          salesRepresentative={orderFullInfo?.salesRepresentative}
          loading={loading}
          testId={testId}
          isEditable
        />
      )
    case 'ORDER_MINIMUMS':
      return (
        <OrderMinimumsWidget
          minAmount={orderFullInfo?.brand.orderSettings?.minAmount || 0}
          minUnits={orderFullInfo?.brand?.orderSettings?.minUnits || 0}
          quantity={orderFullInfo?.quantity || 0}
          wholesaleTotal={orderFullInfo?.wholesaleTotal} //TODO @palvarez: change to use financials dueTotal maxPrice
          currency={orderFullInfo?.priceType?.currency}
          loading={loading}
          testId={testId}
        />
      )
    case 'PRODUCTS':
      return (
        <ProductsWidget
          id={orderFullInfo?.id || ''}
          webOrderLookupId={orderFullInfo?.webOrderLookupId || ''}
          status={orderFullInfo?.status}
          products={products}
          productAmounts={productAmounts}
          fetchAllProducts={fetchAllProducts}
          collections={orderFullInfo?.collections?.edges}
          priceType={orderFullInfo?.priceType}
          brand={orderFullInfo?.brand}
          testId={testId}
          setIsProductsModalOpen={setIsProductsModalOpen}
          loading={loading}
        />
      )
    case 'FINANCIAL_SUMMARY':
      return (
        <FinancialSummaryContainer
          id={orderFullInfo?.id || ''}
          brand={orderFullInfo?.brand}
          financials={orderFullInfo?.financials}
          status={orderFullInfo?.status}
          priceType={orderFullInfo?.priceType}
          loading={loading}
          testId={testId}
        />
      )
    case 'ADDRESSES':
      return (
        <AddressesWidget
          brand={orderFullInfo?.brand}
          retailerProfile={orderFullInfo?.retailerProfile}
          orderStatus={orderFullInfo?.status}
          displayShippingCode={
            !!orderFullInfo?.brand?.orderSettings?.displayShippingCode
          }
          isRetailerRestrictedFromEditing={
            Boolean(
              orderFullInfo?.brand?.orderSettings
                ?.retailerRestrictedFromEditing,
            ) ||
            Boolean(
              orderFullInfo?.brand?.orderSettings
                ?.restrictEditNotesAndReturnedOrders,
            )
          }
          isOrderOrphan={!!orderFullInfo?.isOrphan}
          loading={loading}
          testId={testId}
        />
      )
    case 'ORDER_DETAILS':
      return (
        <OrderDetailsWidget
          details={{
            id: orderFullInfo?.id || '',
            brand: orderFullInfo?.brand,
            status: orderFullInfo?.status,
            collections: orderFullInfo?.collections,
            deliveryWindow: orderFullInfo?.collections?.deliveryWindow,
            warehouses: orderFullInfo?.warehouses,
            divisions: orderFullInfo?.divisions,
            retailerProfile: orderFullInfo?.retailerProfile,
            types: orderFullInfo?.types,
            created: orderFullInfo?.created,
            season: orderFullInfo?.season,
            event: orderFullInfo?.event,
          }}
          loading={loading}
          refetch={refetch}
          testId={testId}
        />
      )
    case 'LEGAL':
      return (
        <LegalWidget
          testId={testId}
          loading={loading}
          termsAndConditions={orderFullInfo?.termsAndConditions}
          retailerProfile={orderFullInfo?.retailerProfile}
          brandName={orderFullInfo?.brand?.name || ''}
        />
      )
    case 'NOTES':
      return (
        <NotesWidget
          testId={testId}
          id={orderFullInfo?.id || ''}
          status={orderFullInfo?.status}
          internalComments={orderFullInfo?.internalComments || ''}
          loading={loading}
          isEditable
        />
      )
    default:
      return <div />
  }
}

type Layout = Record<'sm' | 'md' | 'lg', WidgetType[][][]>

const liteRetailerWithOrderMinimumsColumns: Layout = {
  sm: [
    [['CONTACTS'], ['ADDRESSES'], ['PRODUCTS'], ['ORDER_DETAILS']],
    [['FINANCIAL_SUMMARY'], ['ORDER_MINIMUMS'], ['LEGAL']],
  ],
  md: [
    [['ADDRESSES'], ['PRODUCTS']],
    [
      ['CONTACTS'],
      ['FINANCIAL_SUMMARY'],
      ['ORDER_DETAILS'],
      ['LEGAL'],
      ['ORDER_MINIMUMS'],
    ],
  ],
  lg: [
    [['ADDRESSES'], ['PRODUCTS']],
    [['CONTACTS'], ['FINANCIAL_SUMMARY'], ['ORDER_MINIMUMS']],
    [['ORDER_DETAILS'], ['LEGAL']],
  ],
}
const liteRetailerWithoutMinimumsColumns: Layout = {
  sm: [
    [['CONTACTS'], ['ADDRESSES'], ['PRODUCTS'], ['ORDER_DETAILS']],
    [['FINANCIAL_SUMMARY'], ['LEGAL']],
  ],
  md: [
    [['ADDRESSES'], ['PRODUCTS']],
    [['CONTACTS'], ['FINANCIAL_SUMMARY'], ['ORDER_DETAILS'], ['LEGAL']],
  ],
  lg: [
    [['ADDRESSES'], ['PRODUCTS']],
    [['CONTACTS'], ['FINANCIAL_SUMMARY']],
    [['ORDER_DETAILS'], ['LEGAL']],
  ],
}
const liteRetailerWithOrderMinimumsAndWithoutLegalColumns: Layout = {
  sm: [
    [['CONTACTS'], ['ADDRESSES'], ['PRODUCTS'], ['ORDER_DETAILS']],
    [['FINANCIAL_SUMMARY'], ['LEGAL']],
  ],
  md: [
    [['ADDRESSES'], ['PRODUCTS']],
    [['CONTACTS'], ['FINANCIAL_SUMMARY'], ['ORDER_DETAILS'], ['LEGAL']],
  ],
  lg: [
    [['ADDRESSES'], ['PRODUCTS']],
    [['CONTACTS'], ['FINANCIAL_SUMMARY']],
    [['ORDER_DETAILS'], ['LEGAL']],
  ],
}
const liteRetailerWithoutMinimumsAndLegalColumns: Layout = {
  sm: [
    [['CONTACTS'], ['ORDER_DETAILS']],
    [['PRODUCTS'], ['FINANCIAL_SUMMARY'], ['ADDRESSES']],
  ],
  md: [
    [['ADDRESSES'], ['PRODUCTS']],
    [['CONTACTS'], ['FINANCIAL_SUMMARY'], ['ORDER_DETAILS']],
  ],
  lg: [
    [['ADDRESSES'], ['PRODUCTS']],
    [['CONTACTS'], ['FINANCIAL_SUMMARY']],
    [['ORDER_DETAILS']],
  ],
}
const proBrandWithOrderMinimumsColumns: Layout = {
  sm: [
    [['CONTACTS'], ['ADDRESSES'], ['PRODUCTS'], ['ORDER_DETAILS']],
    [
      ['FINANCIAL_SUMMARY'],
      ['PRICE_CHANGES'],
      ['ORDER_MINIMUMS'],
      ['LEGAL'],
      ['NOTES'],
    ],
  ],
  md: [
    [['ADDRESSES'], ['PRODUCTS']],
    [
      ['CONTACTS'],
      ['FINANCIAL_SUMMARY'],
      ['PRICE_CHANGES'],
      ['ORDER_DETAILS'],
      ['LEGAL'],
      ['NOTES'],
      ['ORDER_MINIMUMS'],
    ],
  ],
  lg: [
    [['ADDRESSES'], ['PRODUCTS']],
    [
      ['CONTACTS'],
      ['FINANCIAL_SUMMARY'],
      ['PRICE_CHANGES'],
      ['ORDER_MINIMUMS'],
    ],
    [['ORDER_DETAILS'], ['LEGAL'], ['NOTES']],
  ],
}
const proBrandWithoutMinimumsColumns: Layout = {
  sm: [
    [['CONTACTS'], ['ADDRESSES'], ['PRODUCTS'], ['ORDER_DETAILS']],
    [['FINANCIAL_SUMMARY'], ['PRICE_CHANGES'], ['LEGAL'], ['NOTES']],
  ],
  md: [
    [['ADDRESSES'], ['PRODUCTS']],
    [
      ['CONTACTS'],
      ['FINANCIAL_SUMMARY'],
      ['PRICE_CHANGES'],
      ['ORDER_DETAILS'],
      ['LEGAL'],
      ['NOTES'],
    ],
  ],
  lg: [
    [['ADDRESSES'], ['PRODUCTS']],
    [['CONTACTS'], ['FINANCIAL_SUMMARY'], ['PRICE_CHANGES']],
    [['ORDER_DETAILS'], ['LEGAL'], ['NOTES']],
  ],
}
const proBrandWithOrderMinimumsWithoutLegalColumns: Layout = {
  sm: [
    [['CONTACTS'], ['ADDRESSES'], ['PRODUCTS'], ['ORDER_DETAILS']],
    [['FINANCIAL_SUMMARY'], ['PRICE_CHANGES'], ['ORDER_MINIMUMS'], ['NOTES']],
  ],
  md: [
    [['ADDRESSES'], ['PRODUCTS']],
    [
      ['CONTACTS'],
      ['FINANCIAL_SUMMARY'],
      ['PRICE_CHANGES'],
      ['ORDER_DETAILS'],
      ['NOTES'],
      ['ORDER_MINIMUMS'],
    ],
  ],
  lg: [
    [['ADDRESSES'], ['PRODUCTS']],
    [
      ['CONTACTS'],
      ['FINANCIAL_SUMMARY'],
      ['PRICE_CHANGES'],
      ['ORDER_MINIMUMS'],
    ],
    [['ORDER_DETAILS'], ['NOTES']],
  ],
}

const proBrandWithoutMinimumsAndLegalColumns: Layout = {
  sm: [
    [['CONTACTS'], ['ADDRESSES'], ['PRODUCTS'], ['ORDER_DETAILS']],
    [['FINANCIAL_SUMMARY'], ['PRICE_CHANGES'], ['NOTES']],
  ],
  md: [
    [['ADDRESSES'], ['PRODUCTS']],
    [
      ['CONTACTS'],
      ['FINANCIAL_SUMMARY'],
      ['PRICE_CHANGES'],
      ['ORDER_DETAILS'],
      ['NOTES'],
    ],
  ],
  lg: [
    [['ADDRESSES'], ['PRODUCTS']],
    [['CONTACTS'], ['FINANCIAL_SUMMARY'], ['PRICE_CHANGES']],
    [['ORDER_DETAILS'], ['NOTES']],
  ],
}

const Container = styled.div`
  position: relative;
  gap: 16px;
  margin: 12px 16px 16px 16px;
  display: grid;
  grid-template-columns: 1fr 1fr;

  @media (min-width: 1024px) {
    grid-template-columns: 3fr 1fr;
  }
  @media (min-width: 1920px) {
    grid-template-columns: 4fr 1fr 1fr;
  }
`

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  & > div {
    width: 100%;
  }
`

const Row = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 16px;

  & > div {
    width: 100%;
  }
`

const StyledContactsWidget = styled(ContactsWidget)`
  overflow: visible;
  & > div:nth-child(2) {
    overflow: visible;
  }
`
